import React, { useCallback, useEffect, useRef, useState } from 'react'
import { useNavigate, useParams, useSearchParams } from 'react-router-dom'
import { useEvent, useEventsOnViewLoad } from '@emerald-works/react-event-bus-client'
import { v4 as uuidv4 } from 'uuid'
import {
  Box,
  Button,
  CircularProgress,
  Divider,
  Grid,
  Switch,
  TextField,
  Typography
} from '@mui/material'
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline'
import { ActionButton, FileUploader, ImageUploader } from '@emerald-works-nova/components'
import { Timeline } from '@mui/icons-material'
import { BannerStatic, Panel, SectionHeader } from '../../components'
import { routes } from '../../routes'
import { usePromiseEvent } from '../../hooks'
import { useStyles } from './style'

const STEP = {
  UPLOAD: 'UPLOAD',
  PROCESSING: 'PROCESSING',
  ERROR: 'ERROR',
  METADATA: 'METADATA'
}

// Mirrors the hook in content-edit/components/body/index.js
const useScormCloudCallbacks = ({ contentId }) => {
  const scormGetImportJobStatus = usePromiseEvent('scormGetImportJobStatus')
  const scormCreateFetchAndImportCourse = usePromiseEvent('scormCreateFetchAndImportCourse')

  const createFetchAndImportCourse = useCallback(async ({ scormS3Key }) => {
    return scormCreateFetchAndImportCourse({ courseId: contentId, scormS3Key })
  }, [scormCreateFetchAndImportCourse, contentId])

  const getImportJobStatus = useCallback(async (importJobId) => {
    return scormGetImportJobStatus({ importJobId })
  }, [scormGetImportJobStatus])

  return { createFetchAndImportCourse, getImportJobStatus }
}

const SkillsPracticeEdit = () => {
  const classes = useStyles()
  const navigate = useNavigate()
  const { moduleId: editModuleId } = useParams()
  const [searchParams] = useSearchParams()
  const isEdit = Boolean(editModuleId)

  // moduleId: edit → URL param; create → query param (retry) or fresh UUID
  const [moduleId] = useState(() => {
    if (isEdit) return editModuleId
    return searchParams.get('moduleId') || uuidv4()
  })

  const [step, setStep] = useState(isEdit ? STEP.METADATA : STEP.UPLOAD)
  const [showReuploader, setShowReuploader] = useState(false)
  const [title, setTitle] = useState('')
  const [description, setDescription] = useState('')
  const [image, setImage] = useState(null)
  const [isActive, setIsActive] = useState(true)
  const [scormCourseName, setScormCourseName] = useState('')
  const [isSaving, setIsSaving] = useState(false)
  const [isManifestPending, setIsManifestPending] = useState(false)

  // Ref to avoid stale closures in event callbacks
  const updatePurposeRef = useRef(null) // 'manifest' | 'final'
  const pollingIntervalRef = useRef(null)

  const scormCallbacks = useScormCloudCallbacks({ contentId: moduleId })

  // --- EventBus events ---

  const [createModule] = useEvent([
    {
      eventName: 'adminSkillsPracticeCreate',
      onSuccess: () => {},
      onError: () => {
        BannerStatic.show({ label: 'Failed to register SCORM upload. Please try again.', isError: true })
        setStep(STEP.ERROR)
      }
    }
  ])

  const [updateModule] = useEvent([
    {
      eventName: 'adminSkillsPracticeUpdate',
      onSuccess: (payload) => {
        if (updatePurposeRef.current === 'manifest') {
          // Manifest auto-fill complete — transition to metadata
          setTitle(payload.module?.title || '')
          setDescription(payload.module?.description || '')
          setScormCourseName(payload.module?.title || '')
          setStep(STEP.METADATA)
          setShowReuploader(false)
          setIsManifestPending(false)
        } else {
          // Final save complete
          BannerStatic.show({ label: 'Module saved successfully.' })
          navigate(routes.skillsPracticeList.path)
        }
        setIsSaving(false)
        updatePurposeRef.current = null
      },
      onError: () => {
        BannerStatic.show({ label: 'Failed to save module. Please try again.', isError: true })
        setIsSaving(false)
        setIsManifestPending(false)
        updatePurposeRef.current = null
      }
    }
  ])

  const [getModule] = useEvent([
    {
      eventName: 'adminSkillsPracticeGet',
      onSuccess: (payload) => {
        const module = payload?.module
        if (module) {
          setTitle(module.title || '')
          setDescription(module.description || '')
          setImage(module.image || null)
          setIsActive(module.status === 'active')
          setScormCourseName(module.title || '')
        }
      },
      onError: () => {
        BannerStatic.show({ label: 'Failed to load module.', isError: true })
        navigate(routes.skillsPracticeList.path)
      }
    }
  ])

  useEventsOnViewLoad(() => {
    if (isEdit) {
      getModule.trigger({ moduleId })
    }
  }, [getModule, isEdit, moduleId])

  useEffect(() => {
    return () => {
      if (pollingIntervalRef.current) clearInterval(pollingIntervalRef.current)
    }
  }, [])

  // --- SCORM import job polling ---

  const startPolling = useCallback((jobId) => {
    if (pollingIntervalRef.current) clearInterval(pollingIntervalRef.current)

    pollingIntervalRef.current = setInterval(async () => {
      try {
        const result = await scormCallbacks.getImportJobStatus(jobId)

        if (result.status === 'COMPLETE') {
          clearInterval(pollingIntervalRef.current)
          pollingIntervalRef.current = null

          const course = result.importResult?.course
          const manifestTitle = course?.metadata?.title || course?.title || ''
          const manifestDescription = course?.metadata?.description || course?.description || ''

          setIsManifestPending(true)
          updatePurposeRef.current = 'manifest'
          updateModule.trigger({
            moduleId,
            importJobDetails: { id: jobId, status: 'ACTIVE' },
            title: manifestTitle,
            description: manifestDescription
          })
        } else if (result.status === 'ERROR') {
          clearInterval(pollingIntervalRef.current)
          pollingIntervalRef.current = null

          setIsManifestPending(true)
          updatePurposeRef.current = 'manifest'
          updateModule.trigger({
            moduleId,
            importJobDetails: { id: jobId, status: 'ERROR' }
          })
          setStep(STEP.ERROR)
        }
      } catch (err) {
        console.error('Error polling SCORM import status', err)
      }
    }, 10000)
  }, [scormCallbacks, moduleId, updateModule])

  // --- SCORM upload handlers ---

  const handleScormDropAccepted = useCallback(async (file) => {
    setStep(STEP.PROCESSING)
    try {
      const result = await scormCallbacks.createFetchAndImportCourse({ scormS3Key: file.s3Key })
      createModule.trigger({ moduleId, importJobId: result.importJobId })
      startPolling(result.importJobId)
    } catch (err) {
      console.error('SCORM upload error', err)
      setStep(STEP.ERROR)
    }
  }, [scormCallbacks, moduleId, createModule, startPolling])

  const handleReuploadDropAccepted = useCallback(async (file) => {
    setStep(STEP.PROCESSING)
    setShowReuploader(false)
    try {
      const result = await scormCallbacks.createFetchAndImportCourse({ scormS3Key: file.s3Key })
      startPolling(result.importJobId)
    } catch (err) {
      console.error('Re-upload error', err)
      setStep(STEP.METADATA)
    }
  }, [scormCallbacks, startPolling])

  const handleRetry = () => {
    setStep(STEP.UPLOAD)
  }

  const handleCancel = () => {
    navigate(routes.skillsPracticeList.path)
  }

  // --- Final save ---

  const isSaveEnabled = title.trim() && description.trim() && image && !isManifestPending

  const handleSave = () => {
    setIsSaving(true)
    updatePurposeRef.current = 'final'
    updateModule.trigger({
      moduleId,
      title,
      description,
      image,
      status: isActive ? 'active' : 'inactive'
    })
  }

  // --- Render helpers ---

  const renderStep1 = () => {
    if (step === STEP.UPLOAD) {
      return (
        <Box className={classes.stepContainer}>
          <Typography variant='h6' gutterBottom>Step 1 — Upload SCORM Package</Typography>
          <Typography variant='body2' color='text.secondary' gutterBottom>
            Upload a SCORM .zip file. Metadata will be auto-filled from the manifest.
          </Typography>
          <Box mt={3}>
            <FileUploader
              Icon={Timeline}
              accept='.zip'
              label='Drop SCORM package here to upload |'
              description='Only SCORM .zip package files are supported'
              descriptionUpload='Uploading SCORM package...'
              showInlineError
              storageOpts={{ level: 'private' }}
              onDropAccepted={handleScormDropAccepted}
              dataTest='skills-practice-scorm-uploader'
            />
          </Box>
        </Box>
      )
    }

    if (step === STEP.PROCESSING) {
      return (
        <Box className={classes.processingContainer}>
          <CircularProgress size={48} />
          <Typography variant='h6'>Processing SCORM Package</Typography>
          <Typography variant='body2' color='text.secondary'>
            This may take a few minutes. Once finished, you must add Title, Description and Image to the new module.
          </Typography>
        </Box>
      )
    }

    if (step === STEP.ERROR) {
      return (
        <Box className={classes.errorContainer}>
          <Typography variant='h6' color='error'>SCORM Import Failed</Typography>
          <Typography variant='body2'>
            The SCORM package could not be imported. You can retry the upload or cancel to return to the list.
          </Typography>
          <Box display='flex' gap={2} mt={1}>
            <Button variant='outlined' color='primary' onClick={handleRetry} data-test='scorm-retry-button'>
              Retry Upload
            </Button>
            <Button variant='outlined' onClick={handleCancel} data-test='scorm-cancel-button'>
              Cancel
            </Button>
          </Box>
        </Box>
      )
    }

    return null
  }

  const renderMetadataFields = () => (
    <Box className={classes.stepContainer}>
      <Typography variant='h6' gutterBottom>
        {isEdit ? 'Edit Module' : 'Step 2 — Module Details'}
      </Typography>

      {/* SCORM completed state */}
      <Box className={classes.scormCompletedBox}>
        <Box display='flex' alignItems='center' gap={1}>
          <CheckCircleOutlineIcon color='success' />
          <Box>
            <Typography variant='body2' fontWeight={600}>SCORM Package Imported</Typography>
            {scormCourseName && (
              <Typography variant='caption' color='text.secondary'>{scormCourseName}</Typography>
            )}
          </Box>
        </Box>
        <Button
          size='small'
          variant='outlined'
          onClick={() => setShowReuploader(v => !v)}
          data-test='skills-practice-reupload-toggle'
        >
          Re-upload
        </Button>
      </Box>

      {showReuploader && (
        <Box mt={2}>
          <FileUploader
            Icon={Timeline}
            accept='.zip'
            label='Drop new SCORM package here |'
            description='Only SCORM .zip package files are supported'
            descriptionUpload='Uploading SCORM package...'
            showInlineError
            storageOpts={{ level: 'private' }}
            onDropAccepted={handleReuploadDropAccepted}
            dataTest='skills-practice-scorm-reuploader'
          />
        </Box>
      )}

      <Box className={classes.fieldGroup}>
        <TextField
          label='Title'
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          fullWidth
          required
          inputProps={{ 'data-test': 'skills-practice-title-input' }}
        />
      </Box>

      <Box className={classes.fieldGroup}>
        <TextField
          label='Description'
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          fullWidth
          required
          multiline
          minRows={3}
          inputProps={{ 'data-test': 'skills-practice-description-input' }}
        />
      </Box>

      <Box className={classes.fieldGroup}>
        <Typography variant='subtitle2' gutterBottom>Module Image *</Typography>
        {!image ? (
          <ImageUploader
            data-test='skills-practice-image-uploader'
            onDropAccepted={(imgInfo) => setImage(imgInfo)}
            imageNamePrefix={`skills-practice/${moduleId}`}
          />
        ) : (
          <Box display='flex' alignItems='center' gap={2} mt={1}>
            {image.imgUrl && (
              <img
                src={image.imgUrl}
                alt='Module thumbnail'
                style={{ width: 80, height: 54, objectFit: 'cover', borderRadius: 4 }}
              />
            )}
            <Box>
              <Typography variant='body2'>{image.name || 'Image uploaded'}</Typography>
              <Button size='small' onClick={() => setImage(null)} data-test='skills-practice-remove-image'>
                Remove
              </Button>
            </Box>
          </Box>
        )}
      </Box>

      {isEdit && (
        <Box className={classes.fieldGroup} display='flex' alignItems='center' gap={2}>
          <Typography variant='subtitle2'>Status</Typography>
          <Switch
            checked={isActive}
            onChange={(e) => setIsActive(e.target.checked)}
            color='primary'
            data-test='skills-practice-status-toggle'
          />
          <Typography variant='body2'>{isActive ? 'Active' : 'Inactive'}</Typography>
        </Box>
      )}

      <Divider sx={{ mt: 3, mb: 2 }} />

      <Box display='flex' justifyContent='flex-end' gap={2}>
        <ActionButton variant='secondary' onClick={handleCancel} disabled={isSaving}>
          Cancel
        </ActionButton>
        <ActionButton
          variant='primary'
          onClick={handleSave}
          disabled={!isSaveEnabled || isSaving}
          isWorking={isSaving}
          dataTest='skills-practice-save-button'
        >
          Save
        </ActionButton>
      </Box>
    </Box>
  )

  if (getModule.isWorking) {
    return (
      <Box display='flex' justifyContent='center' alignItems='center' minHeight={300}>
        <CircularProgress size={60} />
      </Box>
    )
  }

  return (
    <div className={classes.root}>
      <Grid container justifyContent='space-between' alignItems='center' className={classes.header}>
        <Grid item>
          <SectionHeader
            data-test='skills-practice-edit-header'
            title={isEdit ? (title || 'Edit Module') : 'New Module'}
            location='Skills Practice /'
          />
        </Grid>
      </Grid>

      <Grid container>
        <Grid item xs={12}>
          <Panel>
            {step !== STEP.METADATA && renderStep1()}
            {step === STEP.METADATA && renderMetadataFields()}
          </Panel>
        </Grid>
      </Grid>
    </div>
  )
}

export default SkillsPracticeEdit
